<template><div><code-well-header>
  <dt-button> Place Call </dt-button>
</code-well-header>
<!-- <component-combinator component-name="DtButton" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>&lt;button&gt;</code> and link (<code v-pre>&lt;a&gt;</code>) HTML elements each describe a specific intent. Understanding the distinction is important: if it goes somewhere, it's a link (<code v-pre>&lt;a&gt;</code>). If an action occurs, use a Button (<code v-pre>&lt;button&gt;</code>). When you need <strong>button styling with navigation behavior</strong> (CTAs, toolbar actions that navigate), use DtButton's <code v-pre>href</code> or <code v-pre>to</code> props — see <a href="#navigation" class="d-docsite--link d-link">Navigation</a>.</li>
<li class="d-docsite--list-element">Avoid using too many buttons on a page.</li>
<li class="d-docsite--list-element">Set the <code v-pre>type</code> attribute to define its purpose: <code v-pre>submit</code>, <code v-pre>button</code>, or <code v-pre>reset</code>. Browsers default to <code v-pre>submit</code> if it isn't defined, and that cannot be assumed as the preferred behavior.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Conveying that an action that will occur when invoked.</li>
<li class="d-docsite--list-element">To trigger an action or behavior, such as submitting a form or spawning a <RouteLink to="/components/modal.html" class="d-docsite--link d-link">Modal</RouteLink>.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid using to navigate between destinations, deferring to a <RouteLink to="/components/link.html" class="d-docsite--link d-link">Link</RouteLink> instead. Exception: use DtButton with <code v-pre>href</code> or <code v-pre>to</code> when button styling is intentional (e.g. CTAs, toolbar actions).</li>
</ul>
</template>
</dialtone-usage>
<h2 id="writing-guidelines" tabindex="-1" class="d-docsite--header-2">Writing Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#writing-guidelines" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Button labels should be clear and predictable so users have confidence in their actions.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Lead with a strong verb and use <strong>verb + noun</strong> structure except for common actions like “Done,” “Close,” “Cancel,” or “OK”</li>
<li class="d-docsite--list-element">Should be sentence case</li>
<li class="d-docsite--list-element">Do not use punctuation</li>
<li class="d-docsite--list-element">Avoid unnecessary articles such as “the,” “an,” and “a.”</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add number</li>
<li class="d-docsite--list-element">Create menu</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add Number</li>
<li class="d-docsite--list-element">Create a menu</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone provides five options for <code v-pre>kind</code>, with three levels of <code v-pre>importance</code>.</p>
<ButtonVariantsTable></ButtonVariantsTable><h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The base button should be the go-to button for most of your needs. When in doubt, use this style. To help provide clarity to users, it is generally recommended to use only one primary button style within a section or page.</p>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <button class="d-btn d-btn--primary" type="button"><span class="d-btn__label">Place Call</span></button>
      <button class="d-btn d-btn--outlined" type="button"><span class="d-btn__label">Place Call</span></button>
      <button class="d-btn" type="button"><span class="d-btn__label">Place Call</span></button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--primary" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--outlined" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button> Place Call </dt-button>
<dt-button importance="outlined"> Place Call </dt-button>
<dt-button importance="clear"> Place Call </dt-button>
'
showHtmlWarning />
<h3 id="danger" tabindex="-1" class="d-docsite--header-3">Danger <a class="header-anchor d-link d-docsite--link d-link" href="#danger" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The danger button style is used to communicate critical or destructive actions such as deleting content, accounts, or canceling services.</p>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <button class="d-btn d-btn--danger d-btn--primary" type="button"><span class="d-btn__label">Place Call</span></button>
    <button class="d-btn d-btn--danger d-btn--outlined" type="button"><span class="d-btn__label">Place Call</span></button>
    <button class="d-btn d-btn--danger" type="button"><span class="d-btn__label">Place Call</span></button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--danger d-btn--primary" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--danger d-btn--outlined" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--danger" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button kind="danger"> Place Call </dt-button>
<dt-button kind="danger" importance="outlined"> Place Call </dt-button>
<dt-button kind="danger" importance="clear"> Place Call </dt-button>
'
showHtmlWarning />
<h3 id="positive" tabindex="-1" class="d-docsite--header-3">Positive <a class="header-anchor d-link d-docsite--link d-link" href="#positive" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The positive button style is used to communicate positive actions.</p>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-button kind="positive">Place Call</dt-button>
    <dt-button kind="positive" importance="outlined">Place Call</dt-button>
    <dt-button kind="positive" importance="clear">Place Call</dt-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--positive d-btn--primary" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--positive d-btn--outlined" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--positive" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button kind="positive"> Place Call </dt-button>
<dt-button kind="positive" importance="outlined"> Place Call </dt-button>
<dt-button kind="positive" importance="clear"> Place Call </dt-button>
'
showHtmlWarning />
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The inverted button style is used to visually separate buttons set on darker backgrounds.</p>
<code-well-header bgclass="d-bgc-contrast">
  <dt-stack direction="row" gap="400">
    <button class="d-btn d-btn--inverted d-btn--primary" type="button"><span class="d-btn__label">Place Call</span></button>
    <button class="d-btn d-btn--inverted d-btn--outlined" type="button"><span class="d-btn__label">Place Call</span></button>
    <button class="d-btn d-btn--inverted" type="button"><span class="d-btn__label">Place Call</span></button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--inverted d-btn--primary" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--inverted d-btn--outlined" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--inverted" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button kind="inverted"> Place Call </dt-button>
<dt-button kind="inverted" importance="outlined"> Place Call </dt-button>
<dt-button kind="inverted" importance="clear"> Place Call </dt-button>
'
showHtmlWarning />
<h3 id="muted" tabindex="-1" class="d-docsite--header-3">Muted <a class="header-anchor d-link d-docsite--link d-link" href="#muted" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The muted button style is used to communicate non-primary actions for contexts in which the base style may not work
(e.g. colored backgrounds, validation components, etc).
This style’s use should be rare. When in doubt, use the <a href="#default" class="d-docsite--link d-link">default button style</a>.</p>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <button class="d-btn d-btn--muted" type="button"><span class="d-btn__label">Place Call</span></button>
    <button class="d-btn d-btn--muted d-btn--outlined" type="button"><span class="d-btn__label">Place Call</span></button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--muted" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--muted d-btn--outlined" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button kind="muted" importance="clear"> Place Call </dt-button>
<dt-button kind="muted" importance="outlined"> Place Call </dt-button>
'
showHtmlWarning />
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Buttons can be disabled using either the <code v-pre>disabled</code> attribute or a Dialtone class. Use the attribute when a button should appear disabled and not recieve focus; use the class when a button should appear disabled but still recieve focus (i.e. a disabled button with a tooltip). Using the class also requires <code v-pre>aria-disabled</code> and a wrapper to display the &quot;not allowed&quot; pointer. Additional javascript implementation is required to prevent the click event.
All button styles and variations appear the same when disabled.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <span>
      <dt-button disabled>Place Call (disabled attribute)</dt-button>
    </span>
    <span>
      <span class="d-c-not-allowed">
        <dt-button class="d-btn--disabled">Place Call (disabled class)</dt-button>
      </span>
    </span>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<!-- disabled attribute -->
<button class="d-btn" type="button" disabled><span class="d-btn__label">...</span></button>
<!-- disabled class -->
<span class="d-c-not-allowed">
  <button type="button" class="base-button__button d-btn d-btn--primary d-btn--disabled"><span class="d-btn__label base-button__label">...</span></button>
</span>
'
vueCode='
<!-- disabled attribute -->
<dt-button disabled>Place Call</dt-button>
<!-- disabled class -->
<span class="d-c-not-allowed">
  <dt-button class="d-btn--disabled">Place Call</dt-button>
</span>
'
showHtmlWarning />
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Buttons can be set to active state using the <code v-pre>active</code> prop or <code v-pre>.d-btn--active</code> Dialtone class.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-button importance="clear" active>Place Call</dt-button>
    <dt-button active>Place Call</dt-button>
    <dt-button kind="danger" importance="clear" active>Place Call</dt-button>
    <dt-button kind="positive" importance="clear" active>Place Call</dt-button>
    <dt-button kind="inverted" active>Place Call</dt-button>
    <dt-button kind="muted" active>Place Call</dt-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--active" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary d-btn--active" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--danger d-btn--active" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--positive d-btn--active" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--inverted d-btn--primary d-btn--active" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary d-btn--muted d-btn--active" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button importance="clear" active>Place Call</dt-button>
<dt-button active>Place Call</dt-button>
<dt-button kind="danger" importance="clear" active>Place Call</dt-button>
<dt-button kind="positive" importance="clear" active>Place Call</dt-button>
<dt-button kind="inverted" active>Place Call</dt-button>
<dt-button kind="muted" active>Place Call</dt-button>
'
showHtmlWarning />
<h3 id="link" tabindex="-1" class="d-docsite--header-3">Link <a class="header-anchor d-link d-docsite--link d-link" href="#link" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Buttons can be styled as a <RouteLink to="/components/link.html" class="d-docsite--link d-link">Link</RouteLink> in situations for which you need the appearance of a link but behavior of a button. Using the <code v-pre>button</code> element provides a better accessibility experience.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-button link>Place Call</dt-button>
    <dt-button link linkKind="warning">Place Call</dt-button>
    <dt-button link linkKind="danger">Place Call</dt-button>
    <dt-button link linkKind="success">Place Call</dt-button>
    <dt-button link linkKind="muted">Place Call</dt-button>
    <dt-button link disabled>Place Call</dt-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-link" type="button"> <span class="d-btn__label">Place Call</span></button>
<button class="d-link d-link--warning" type="button"> <span class="d-btn__label">Place Call</span></button>
<button class="d-link d-link--danger" type="button"> <span class="d-btn__label">Place Call</span></button>
<button class="d-link d-link--success" type="button"> <span class="d-btn__label">Place Call</span></button>
<button class="d-link d-link--muted" type="button"> <span class="d-btn__label">Place Call</span></button>
<button class="d-link" type="button" disabled=""> <span class="d-btn__label">Place Call</span></button>
'
vueCode='
<dt-button link>Place Call</dt-button>
<dt-button link linkKind="warning">Place Call</dt-button>
<dt-button link linkKind="danger">Place Call</dt-button>
<dt-button link linkKind="success">Place Call</dt-button>
<dt-button link linkKind="muted">Place Call</dt-button>
<dt-button link disabled>Place Call</dt-button>
'
showHtmlWarning />
<h3 id="unstyled" tabindex="-1" class="d-docsite--header-3">Unstyled <a class="header-anchor d-link d-docsite--link d-link" href="#unstyled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The unstyled button removes all default Dialtone styling while preserving the semantic HTML <code v-pre>&lt;button&gt;</code> element and maintaining proper button behavior and accessibility.</p>
<code-well-header>
    <dt-button kind="unstyled">Place Call</dt-button>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn--unstyled" type="button">Place Call</button>
'
vueCode='
<dt-button kind="unstyled">Place Call</dt-button>
'
showHtmlWarning />
<h2 id="navigation" tabindex="-1" class="d-docsite--header-2">Navigation <a class="header-anchor d-link d-docsite--link d-link" href="#navigation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">DtButton can render as an <code v-pre>&lt;a&gt;</code> or <code v-pre>&lt;router-link&gt;</code> for cases where you need button styling with navigation behavior.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>Navigating within the app?</strong> Use <code v-pre>to</code>. Renders <code v-pre>&lt;router-link&gt;</code> for client-side navigation without page reloads.</li>
<li class="d-docsite--list-element"><strong>Linking to an external site?</strong> Use <code v-pre>href</code>. Renders <code v-pre>&lt;a&gt;</code> for standard browser navigation.</li>
<li class="d-docsite--list-element"><strong>Triggering an action?</strong> Use neither. Renders <code v-pre>&lt;button&gt;</code> (default).</li>
</ul>
<h3 id="href" tabindex="-1" class="d-docsite--header-3">href <a class="header-anchor d-link d-docsite--link d-link" href="#href" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Pass <code v-pre>href</code> to render as an <code v-pre>&lt;a&gt;</code> element. Use <code v-pre>target=&quot;_blank&quot;</code> and <code v-pre>rel=&quot;noopener noreferrer&quot;</code> for external links.</p>
<code-well-header>
  <dt-button
    href="https://dialtone.dialpad.com"
    target="_blank"
    rel="noopener noreferrer"
    kind="muted"
    importance="outlined"
    size="sm"
    icon-position="right"
    ref="hrefExample1"
  >
    <template #icon="{ iconSize }">
      <dt-icon name="external-link" :size="iconSize" />
    </template>
    Dialtone
  </dt-button>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.hrefExample1'
vueCode='
<dt-button
  href="<https://dialtone.dialpad.com>"
  target="_blank"
  rel="noopener noreferrer"
  kind="muted"
  importance="outlined"
  size="sm"
  icon-position="right"
>
  <template #icon="{ iconSize }">
    <dt-icon name="external-link" :size="iconSize" />
  </template>
  Dialtone
</dt-button>
'
showHtmlWarning />
<h3 id="to" tabindex="-1" class="d-docsite--header-3">to <a class="header-anchor d-link d-docsite--link d-link" href="#to" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Pass <code v-pre>to</code> to render as <code v-pre>&lt;router-link&gt;</code> for internal client-side SPA navigation. Use <code v-pre>replace</code> to navigate without adding a history entry.</p>
<code-well-header>
  <dt-button to="/" kind="default" size="xs" ref="toExample2">
    Home
  </dt-button>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.toExample1'
vueCode='
<dt-button to="/" kind="default" size="xs">
  Home
</dt-button>
'
/>
<h3 id="migration" tabindex="-1" class="d-docsite--header-3">Migration <a class="header-anchor d-link d-docsite--link d-link" href="#migration" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you have existing <code v-pre>&lt;a class=&quot;d-btn&quot;&gt;</code> or <code v-pre>&lt;router-link class=&quot;d-btn&quot;&gt;</code> workarounds, replace them with DtButton props:</p>
<code-example-tabs
vueCode='
<!-- Before: raw <a> with manual d-btn classes -->
<a
  class="d-btn d-btn--primary d-btn--outlined d-btn--sm"
  href="<https://example.com>"
  target="_blank"
  rel="noopener noreferrer"
>
  Link Text
</a>
<!-- After: DtButton with href prop -->
<dt-button
  href="<https://example.com>"
  target="_blank"
  rel="noopener noreferrer"
  importance="outlined"
  size="sm"
>
  Link Text
</dt-button>
'
/>
<code-example-tabs
vueCode='
<!-- Before: raw <router-link> with manual d-btn classes -->
<router-link
  class="d-btn d-btn--primary d-btn--sm"
  :to="roomPath"
>
  Join Room
</router-link>
<!-- After: DtButton with to prop -->
<dt-button :to="roomPath" size="sm">
  Join Room
</dt-button>
'
/>
<h2 id="split-button" tabindex="-1" class="d-docsite--header-2">Split Button <a class="header-anchor d-link d-docsite--link d-link" href="#split-button" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <RouteLink to="/components/split-button.html" class="d-docsite--link d-link">Split Button</RouteLink> is its own component containing multiple buttons.</p>
<code-well-header>
  <dt-split-button
    omega-tooltip-text="More calling options"
  >
    Place call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
</code-well-header>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The default button size is <code v-pre>md</code>, but does not need to be explicitly specified.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <span>
      <dt-button size="xs"> Place Call </dt-button>
    </span>
    <span>
      <dt-button size="sm"> Place Call </dt-button>
    </span>
    <span>
      <dt-button> Place Call </dt-button>
    </span>
    <span>
      <dt-button size="lg"> Place Call </dt-button>
    </span>
    <span>
      <dt-button size="xl"> Place Call </dt-button>
    </span>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--primary d-btn--xs" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary d-btn--sm" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary d-btn--lg" type="button"><span class="d-btn__label">...</span></button>
<button class="d-btn d-btn--primary d-btn--xl" type="button"><span class="d-btn__label">...</span></button>
'
vueCode='
<dt-button size="xs"> Place Call </dt-button>
<dt-button size="sm"> Place Call </dt-button>
<dt-button> Place Call </dt-button>
<dt-button size="lg"> Place Call </dt-button>
<dt-button size="xl"> Place Call </dt-button>
'
showHtmlWarning />
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<h3 id="icon-and-label" tabindex="-1" class="d-docsite--header-3">Icon and Label <a class="header-anchor d-link d-docsite--link d-link" href="#icon-and-label" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Button labels can include an icon next to the text. Every button style can accept icon classes, though we only provide a few possible examples. <code v-pre>icon-position</code> can be <code v-pre>left</code> (default), <code v-pre>right</code>, <code v-pre>top</code>, <code v-pre>bottom</code>.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <span>
      <dt-button importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
        Label
      </dt-button>
    </span>
    <span>
      <dt-button importance="outlined" icon-position="top">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
        Label
      </dt-button>
    </span>
    <span>
      <dt-button importance="outlined" icon-position="bottom">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
        Label
      </dt-button>
    </span>
    <span>
      <dt-button importance="outlined" icon-position="right">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
        Label
      </dt-button>
    </span>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--outlined" type="button">
  <span class="d-btn__icon d-btn__icon--left">...</span>
  <span class="d-btn__label">...</span>
</button>
<button class="d-btn d-btn--vertical d-btn--outlined" type="button">
  <span class="d-btn__icon d-btn__icon--top">...</span>
  <span class="d-btn__label">...</span>
</button>
<button class="d-btn d-btn--outlined" type="button">
  <span class="d-btn__icon d-btn__icon--right">...</span>
  <span class="d-btn__label">...</span>
</button>
'
vueCode='
<dt-button importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
  Label
</dt-button>
<dt-button importance="outlined" icon-position="top">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
  Label
</dt-button>
<dt-button importance="outlined" icon-position="bottom">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
  Label
</dt-button>
<dt-button importance="outlined" icon-position="right">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
  Label
</dt-button>
'
showHtmlWarning />
<h3 id="icon-only" tabindex="-1" class="d-docsite--header-3">Icon Only <a class="header-anchor d-link d-docsite--link d-link" href="#icon-only" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Icon-only buttons are commonly used for toggling actions, navigation, or closing UI elements.</p>
<code-well-header>
  <dt-stack
    gap="600"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" kind="danger" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="danger" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="danger">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" importance="clear" kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" importance="outlined" kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400" class="d-bgc-contrast d-p8">
      <dt-button v-dt-tooltip="`Tooltip`" kind="inverted" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="inverted" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" kind="inverted">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--icon-only" type="button">...</button>
<button class="d-btn d-btn--icon-only d-btn--outlined" type="button">...</button>
'
vueCode='
<dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="muted" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="danger" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="danger" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="danger">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" importance="clear" kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" importance="outlined" kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="inverted" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="inverted" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" kind="inverted">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
'
showHtmlWarning />
<h4 class="d-docsite--header-4">Circle</h4>
<p class="d-docsite--paragraph">The following styles are available as a circle shape.</p>
<code-well-header>
  <dt-stack
    gap="600"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="danger" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="danger" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="danger">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button v-dt-tooltip="`Tooltip`" circle importance="clear" kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle importance="outlined" kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="positive">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400" class="d-bgc-contrast d-p8">
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted" importance="clear">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted" importance="outlined">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted">
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--circle btn--inverted" type="button">...</button>
<button class="d-btn d-btn--circle btn--inverted d-btn--outlined" type="button">...</button>
<button class="d-btn d-btn--circle btn--inverted d-btn--primary" type="button">...</button>
'
vueCode='
<dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="muted" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="danger" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="danger" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="danger">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle importance="clear" kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle importance="outlined" kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="positive">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted" importance="clear">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted" importance="outlined">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle kind="inverted">
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
'
showHtmlWarning />
<h2 id="loading" tabindex="-1" class="d-docsite--header-2">Loading <a class="header-anchor d-link d-docsite--link d-link" href="#loading" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Loading buttons are useful for communicating a delay between the button interaction and its action taking place. Every button style can accept the loading button class, though we only provide a few possible examples.</p>
<h3 id="replace-button-label" tabindex="-1" class="d-docsite--header-3">Replace button label <a class="header-anchor d-link d-docsite--link d-link" href="#replace-button-label" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The width of the button remains determined by the length of the label, which is visually hidden in this state.</p>
<code-well-header>
  <dt-stack
    gap="600"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-stack direction="row" gap="400">
      <dt-button loading> Place Call </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" loading>
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button v-dt-tooltip="`Tooltip`" circle loading>
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
    <dt-stack direction="row" gap="400">
      <dt-button kind="muted" importance="outlined" loading> Place Call </dt-button>
      <dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" loading>
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
      <dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" circle loading>
        <template #icon>
          <dt-icon
            name="phone"
            size="300"
          />
        </template>
      </dt-button>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--loading d-btn--primary" type="button"><span class="d-btn__label">Place Call</span></button>
<button class="d-btn d-btn--loading d-btn--outlined" type="button"><span class="d-btn__label">Place Call</span></button>
<button class="d-btn d-btn--danger d-btn--loading" type="button"><span class="d-btn__label">Place Call</span></button>
'
vueCode='
<dt-button loading> Place Call </dt-button>
<dt-button v-dt-tooltip="`Tooltip`" loading>
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button v-dt-tooltip="`Tooltip`" circle loading>
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button kind="muted" importance="outlined" loading> Place Call </dt-button>
<dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" loading>
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
<dt-button kind="muted" importance="outlined" v-dt-tooltip="`Tooltip`" circle loading>
  <template #icon>
    <dt-icon
      name="phone"
      size="300"
    />
  </template>
</dt-button>
'
showHtmlWarning />
<h3 id="with-label" tabindex="-1" class="d-docsite--header-3">With label <a class="header-anchor d-link d-docsite--link d-link" href="#with-label" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack
    gap="400"
    direction="row"
  >
  <dt-button
    icon-position="right"
    size="xs"
  >
    Validating
    <template #icon="{ iconSize }">
      <dt-loader
        :size="iconSize"
      />
    </template>
  </dt-button>
  <dt-button
    icon-position="right"
    size="sm"
  >
    Validating
    <template #icon="{ iconSize }">
      <dt-loader
        :size="iconSize"
      />
    </template>
  </dt-button>
  <dt-button
    icon-position="right"
    size="md"
  >
    Validating
    <template #icon="{ iconSize }">
      <dt-loader
        :size="iconSize"
      />
    </template>
  </dt-button>
  <dt-button
    icon-position="right"
    size="lg"
  >
    Validating
    <template #icon="{ iconSize }">
      <dt-loader
        :size="iconSize"
      />
    </template>
  </dt-button>
</dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<button class="d-btn d-btn--primary d-btn--sm" type="button">
  <span class="d-btn__icon d-btn__icon--right">
    <div class="d-loader" aria-label="loading">
      <svg class="d-icon--size-200 d-icon d-icon--loading d-loader__icon" ...>
        ...
      </svg>
    </div>
  </span>
  <span class="d-btn__label">
    Validating
  </span>
</button>
'
vueCode='
<dt-button icon-position="right">
  Validating
  <template #icon="{ iconSize }">
    <dt-loader :size="iconSize" />
  </template>
</dt-button>
'
showHtmlWarning />
<h2 id="branded" tabindex="-1" class="d-docsite--header-2">Branded <a class="header-anchor d-link d-docsite--link d-link" href="#branded" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We provide the following branded buttons for log-in and sign-up workflows.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <span><button class="d-btn d-btn--brand d-btn--google d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="google-glyph" /></span><span class="d-btn__label">Log in with Google</span></button></span>
    <span><button class="d-btn d-btn--brand d-btn--o365 d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="office-365" /></span><span class="d-btn__label">Log in with Office365</span></button></span>
    <span><button class="d-btn d-btn--brand d-btn--linkedin d-w100p" type="button"><span class="d-btn__icon"><dt-icon name="linkedin" /></span><span class="d-btn__label">Log in with LinkedIn</span></button></span>
  </dt-stack>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>button</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn d-btn--brand d-btn--google<span class="token punctuation">"</span></span> <span class="token attr-name">type</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>button<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__icon<span class="token punctuation">"</span></span><span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>icon-google-glyph</span> <span class="token punctuation">/></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__label<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Log in with Google<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>button</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>button</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn d-btn--brand d-btn--o365<span class="token punctuation">"</span></span> <span class="token attr-name">type</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>button<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__icon<span class="token punctuation">"</span></span><span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>icon-google-glyph</span> <span class="token punctuation">/></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__label<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Log in with Office365<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>button</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>button</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn d-btn--brand d-btn--linkedin<span class="token punctuation">"</span></span> <span class="token attr-name">type</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>button<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__icon<span class="token punctuation">"</span></span><span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>icon-google-glyph</span> <span class="token punctuation">/></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-btn__label<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>Log in with LinkedIn<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>button</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="button" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Choosing between Link and Button elements is paramount for screenreaders to inform the user what will occur. For example: will it go somewhere (Link) or will something happen (Button)?</li>
<li class="d-docsite--list-element">Do not rely on color alone to convey the intent of the button. Defer to the button text as primary way to convey the buttons intent.</li>
<li class="d-docsite--list-element">Display a visible focus state when users tab to them.</li>
<li class="d-docsite--list-element">Use standard semantic usage of HTML elements.</li>
<li class="d-docsite--list-element">Be aware of how screenreaders handle buttons and links differently. For example, both the <code v-pre>Enter</code> and <code v-pre>Space</code> keys triggers a button, while links are triggered only by the <code v-pre>Enter</code> key.</li>
<li class="d-docsite--list-element">If it is a button type while focused:
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Pressing the <code v-pre>Enter</code> or <code v-pre>Space</code> key should trigger the action.</li>
<li class="d-docsite--list-element">Pressing the <code v-pre>Tab</code> key moves focus to the next focusable element.</li>
<li class="d-docsite--list-element">Pressing the <code v-pre>Shift+Tab</code> key moves focus to the previous focusable element.</li>
</ul>
</li>
<li class="d-docsite--list-element">When using DtButton with <code v-pre>href</code> or <code v-pre>to</code>, the component automatically handles Spacebar activation and disabled state (<code v-pre>aria-disabled</code>, <code v-pre>tabindex=&quot;-1&quot;</code>). Navigating elements keep their native link role — <code v-pre>role=&quot;button&quot;</code> is not added because the element navigates rather than performing an in-page action.</li>
</ul>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="button"></component-class-table></div></template>


<script setup>
import ButtonVariantsTable from '@baseComponents/ButtonVariantsTable.vue';
</script>